import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Shield, Award, Download, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getPaymentPlans, createRazorpayOrder, checkSubscriptionStatus } from '../../lib/payments';
import Card, { CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from 'react-toastify';
import { env } from '../../lib/env';
import { trackEvent, trackPurchase, trackException, setUserProperties } from '../../lib/analytics';

const Premium: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [paymentPlans, setPaymentPlans] = useState<any[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  useEffect(() => {
    // Track page visit
    trackEvent('premium_page', 'visit', userProfile?.is_premium ? 'existing_premium' : 'non_premium');
  }, [userProfile?.is_premium]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const plans = await getPaymentPlans();
        setPaymentPlans(plans);
        
        if (user && user.phoneNumber) {
          const subscription = await checkSubscriptionStatus(user.phoneNumber);
          setHasActiveSubscription(!!subscription);
        }
        
        trackEvent('premium_page', 'plans_loaded', plans.length.toString());
      } catch (error: any) {
        console.error('Error fetching data:', error);
        setError('Failed to load payment plans. Please try again.');
        
        trackEvent('premium_page', 'error', 'plans_load_failed');
        trackException(`Premium plans load failed: ${error.message}`, false);
      } finally {
        setIsLoadingPlans(false);
      }
    };
    
    fetchData();
  }, [user]);

  const handleSubscribe = async (planId: string) => {
    if (!user || !userProfile) {
      navigate('/auth');
      return;
    }

    setLoading(planId);
    setError('');

    try {
      const plan = paymentPlans.find(p => p.plan_id === planId);
      if (!plan) {
        throw new Error('Invalid plan selected');
      }

      trackEvent('subscription_attempt', 'click', plan.plan_id, plan.price);

      // Create order using Edge Function
      const { orderId, amount } = await createRazorpayOrder(
        plan,
        user.phoneNumber,
        userProfile.name,
        userProfile.email
      );

      trackEvent('subscription_attempt', 'order_created', plan.plan_id);

      // Initialize Razorpay
      const options = {
        key: env.razorpay.keyId,
        amount: amount,
        currency: 'INR',
        name: 'AI Pandit Ji',
        description: `${plan.name} Subscription`,
        order_id: orderId,
        prefill: {
          name: userProfile.name,
          contact: user.phoneNumber,
          email: userProfile.email || ''
        },
        notes: {
          plan_id: plan.plan_id
        },
        theme: {
          color: '#673AB7'
        },
        handler: function(response: any) {
          trackEvent('subscription_success', 'complete', plan.plan_id, plan.price);
          
          // Set user properties for GA
          setUserProperties({
            subscription_plan: plan.plan_id,
            subscription_price: plan.price,
            is_premium_user: true
          });
          
          // Redirect to success page with payment details
          const params = new URLSearchParams({
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature || '',
            'notes[plan_id]': plan.plan_id,
            amount: amount.toString()
          });
          window.location.href = `/payment/success?${params.toString()}`;
        },
        modal: {
          ondismiss: function() {
            trackEvent('subscription_cancelled', 'cancel', plan.plan_id);
            setLoading(null);
            toast.info('Payment cancelled. You can try again anytime.');
          },
          escape: true,
          backdropclose: false
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function(response: any) {
        trackEvent('subscription_failed', 'error', plan.plan_id, plan.price);
        trackException(`Payment failed for ${plan.plan_id}: ${response.error.description}`, false);
        toast.error('Payment failed. Please try again.');
        setLoading(null);
      });
      razorpay.open();
    } catch (err: any) {
      console.error('Error in handleSubscribe:', err);
      trackEvent('subscription_error', 'error', err.message || 'Unknown error');
      trackException(`Subscription error: ${err.message}`, false);
      toast.error(err.message || 'Failed to process payment. Please try again.');
      setLoading(null);
    }
  };
  
  // Show loading state while fetching plans
  if (isLoadingPlans) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  // Show premium status if user already has subscription
  if (hasActiveSubscription) {
    return (
      <div className="min-h-screen bg-neutral-50 py-12 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary-100 mb-4">
              <Star className="h-10 w-10 text-secondary-500" />
            </div>
            <h1 className="text-3xl font-bold text-primary-800 font-display">You're a Premium Member!</h1>
            <p className="mt-2 text-neutral-600">
              You already have access to all premium features. Enjoy your celestial journey!
            </p>
          </div>
          
          <Card className="text-left">
            <CardHeader>
              <CardTitle>Your Premium Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Star className="h-5 w-5 text-secondary-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Unlimited readings across all categories</span>
                </li>
                <li className="flex items-start">
                  <Clock className="h-5 w-5 text-secondary-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Daily, weekly, monthly, and yearly predictions</span>
                </li>
                <li className="flex items-start">
                  <Download className="h-5 w-5 text-secondary-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Download PDF reports of all your readings</span>
                </li>
                <li className="flex items-start">
                  <Shield className="h-5 w-5 text-secondary-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Priority support for any questions</span>
                </li>
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={() => {
                  trackEvent('navigation', 'premium_to_dashboard', 'existing_premium');
                  navigate('/');
                }} 
                fullWidth
              >
                Go to Dashboard
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-secondary-100 mb-4"
          >
            <Star className="h-10 w-10 text-secondary-500" />
          </motion.div>
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-3xl font-bold text-primary-800 font-display"
          >
            Unlock Premium Cosmic Insights
          </motion.h1>
          <motion.p
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-2 text-neutral-600 max-w-2xl mx-auto"
          >
            Elevate your astrological journey with unlimited readings, detailed predictions, and exclusive premium features.
          </motion.p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {paymentPlans.map((plan, index) => (
            <motion.div
              key={plan.plan_id}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
            >
              <Card 
                className={`h-full ${plan.is_popular ? 'border-secondary-500 border-2' : ''}`}
                onMouseEnter={() => trackEvent('premium_plan', 'hover', plan.plan_id)}
              >
                {plan.is_popular && (
                  <div className="bg-secondary-500 text-white text-center py-1 text-sm font-medium">
                    MOST POPULAR
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="flex items-center">
                    {plan.duration === 'monthly' && <Clock className="mr-2 h-5 w-5 text-primary-500" />}
                    {plan.duration === 'quarterly' && <Award className="mr-2 h-5 w-5 text-secondary-500" />}
                    {plan.duration === 'yearly' && <Star className="mr-2 h-5 w-5 text-primary-500" />}
                    {plan.name}
                  </CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <p className="text-3xl font-bold">₹{plan.price}</p>
                    <p className="text-neutral-500 text-sm">
                      {plan.duration === 'monthly' && 'per month'}
                      {plan.duration === 'quarterly' && 'per quarter'}
                      {plan.duration === 'yearly' && 'per year'}
                    </p>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-start">
                        <Star className="h-5 w-5 text-success-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button 
                    variant={plan.is_popular ? 'primary' : 'outline'}
                    fullWidth
                    isLoading={loading === plan.plan_id}
                    onClick={() => handleSubscribe(plan.plan_id)}
                  >
                    {plan.is_popular ? 'Best Value' : 'Subscribe'}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>
        
        {error && (
          <div className="mt-8 bg-error-50 border border-error-200 text-error-800 p-4 rounded-lg">
            <p>{error}</p>
          </div>
        )}
        
        <div className="mt-16">
          <h2 className="text-2xl font-semibold mb-6 text-center">Premium Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card onMouseEnter={() => trackEvent('premium_benefits', 'hover', 'unlimited_readings')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                    <Star className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Unlimited Readings</h3>
                  <p className="text-neutral-600 text-sm">
                    Access all types of readings without restrictions
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card onMouseEnter={() => trackEvent('premium_benefits', 'hover', 'pdf_reports')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                    <Download className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">PDF Reports</h3>
                  <p className="text-neutral-600 text-sm">
                    Download and save all your readings for future reference
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card onMouseEnter={() => trackEvent('premium_benefits', 'hover', 'detailed_analysis')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                    <Award className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Detailed Analysis</h3>
                  <p className="text-neutral-600 text-sm">
                    Get more comprehensive and in-depth astrological insights
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card onMouseEnter={() => trackEvent('premium_benefits', 'hover', 'priority_support')}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                    <Shield className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Priority Support</h3>
                  <p className="text-neutral-600 text-sm">
                    Get your questions answered with personalized assistance
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Premium;